import { useEffect, useState } from 'react';
import { civilDayIn, localTimeZone, millisecondsUntilCivilDayChanges } from '../time-zone.ts';
import { quoteForDay } from './daily-quote-model.ts';

interface Props {
  /** Production uses the browser zone; explicit only for deterministic tests. */
  timeZone?: string;
}

function useDailyQuote(timeZone: string | undefined) {
  const resolveZone = () => timeZone ?? localTimeZone() ?? 'UTC';
  // An object deliberately: the first effect writes a fresh snapshot even when
  // its day matches. That reconciles text an embedding server-rendered in a
  // different zone or just before midnight instead of suppressing it forever.
  const [clock, setClock] = useState(() => {
    const zone = resolveZone();
    return { day: civilDayIn(zone), revision: 0 };
  });

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      const now = new Date();
      const zone = resolveZone();
      setClock((current) => ({ day: civilDayIn(zone, now), revision: current.revision + 1 }));
      timer = setTimeout(schedule, millisecondsUntilCivilDayChanges(zone, now));
    };
    const catchUp = () => {
      if (timer !== undefined) clearTimeout(timer);
      schedule();
    };

    schedule();
    window.addEventListener('focus', catchUp);
    document.addEventListener('visibilitychange', catchUp);
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      window.removeEventListener('focus', catchUp);
      document.removeEventListener('visibilitychange', catchUp);
    };
  }, [timeZone]);

  return { quote: quoteForDay(clock.day), revision: clock.revision };
}

/** One quiet, non-interactive card; source research lives beside the corpus. */
export function DailyQuote({ timeZone }: Props) {
  const { quote, revision } = useDailyQuote(timeZone);

  return (
    <figure className="dailyquote" aria-label="Daily motivational quote">
      {/* This app is client-rendered. The suppression also makes the component
          safe in an embedding that hydrates across a local-midnight boundary;
          the effect above immediately reconciles the browser's current day. */}
      <blockquote key={`quote-${revision}`}>
        <p suppressHydrationWarning>{quote.text}</p>
      </blockquote>
      <figcaption key={`speaker-${revision}`}>
        <span aria-hidden="true">— </span>
        <cite suppressHydrationWarning>{quote.speaker}</cite>
      </figcaption>
    </figure>
  );
}
